import { Component, DestroyRef, viewChild, effect, input, model, output, inject } from "@angular/core";
import { catchError, debounceTime, map, Observable, of, Subject, switchMap, tap } from "rxjs";
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from "@angular/material/autocomplete";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SuggestionAddress } from "src/app/shared/contract/Address/AddressSearchResponse";
import { HeaderLocationService } from "src/app/shared/services/header-location.service";
import { Coordinate, ObjectCoordinate } from "src/app/shared/contract/Address/Coordinates";
import { BasemapService } from "../../../basemap/basemap.service";
import { Address } from "src/app/shared/contract/Address/Address";
import { EPSGType } from "src/app/shared/contract/Enum/EPSG";
import { PermissionService } from "src/app/modules/core/services/permission.service";
import { MatFormField, MatPrefix, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { FormsModule } from "@angular/forms";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatOption } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { AddressLookupComponent } from "../address-lookup/address-lookup.component";
import { TranslocoPipe } from "@jsverse/transloco";
import { AddressSearchRequest } from "../../../../shared/contract/Address/address-search-request";
import * as olProj from "ol/proj";

@Component({
  selector: "app-search-input",
  templateUrl: "./search-input.component.html",
  styleUrls: ["./search-input.component.scss"],
  imports: [
    MatFormField,
    MatInput,
    MatAutocompleteTrigger,
    FormsModule,
    MatIconButton,
    MatIcon,
    MatSuffix,
    MatAutocomplete,
    MatOption,
    MatTooltip,
    MatProgressSpinner,
    AddressLookupComponent,
    TranslocoPipe,
    MatPrefix
  ]
})
export class SearchInputComponent {
  addressSearchTerm = input("");
  internalSearchTerm = model("");
  searchTermChanged = output<AddressSearchRequest>();

  headerLocationService = inject(HeaderLocationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly baseMapService = inject(BasemapService);
  private readonly permissionService = inject(PermissionService);

  readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);
  readonly searchAddressDebounceTime = 500;

  selectedIndex = 0;
  isFocused = false;

  private readonly addressSearchChangedSubject = new Subject<string>();
  private readonly addressSearchChanged$ = this.addressSearchChangedSubject
    .asObservable()
    .pipe(debounceTime(this.searchAddressDebounceTime), takeUntilDestroyed(this.destroyRef));

  constructor() {
    this.subscribeToAddressChanged();

    // Effect for syncing the internal search term
    effect(() => {
      const searchTerm = this.addressSearchTerm();
      this.internalSearchTerm.set(searchTerm?.length > 0 ? searchTerm : "");
    });

    // Effect for opening the autocomplete when there are suggestions, address lookup, loading, or error
    effect(() => {
      const hasSuggestions = this.headerLocationService.suggestions()?.length > 0;
      const showSuggestions = this.headerLocationService.showSuggestions();
      const showLookup = this.headerLocationService.showAddressLookup();
      const isLoadingLookup = this.headerLocationService.isLoadingLookup();
      const hasError = this.headerLocationService.hasLookupError();

      if (
        (hasSuggestions && showSuggestions && !showLookup && !isLoadingLookup && !hasError) ||
        showLookup ||
        isLoadingLookup ||
        hasError
      ) {
        this.openAutocomplete();
      }
    });

    // Effect for showing address lookup when lookup response is available
    effect(() => {
      const lookupResponse = this.headerLocationService.lookupResponse();
      if (lookupResponse && this.headerLocationService.selectedSuggestion()) {
        this.headerLocationService.showAddressLookup.set(true);
        this.headerLocationService.showSuggestions.set(false);
        this.openAutocomplete();
      }
    });
  }

  setAddress(address: SuggestionAddress) {
    if (!address) {
      return;
    }

    this.headerLocationService.selectedSuggestion.set(address);

    address.isGlobalSearch = this.headerLocationService.isGlobalSearchMode();
    this.headerLocationService.selectAddress(address);
  }

  createNewObject(type: "residential" | "commercial" | "rvm" | "mixed-real-estate") {
    const newAddress = this.headerLocationService.selectedSuggestion()?.address;

    const createAddress$ = newAddress ? of(newAddress) : this.createAddressWithoutSuggestion();
    createAddress$
      .pipe(
        tap(address => {
          this.headerLocationService.navigateToObject(address, null, type);
          this.closeAutocomplete();
          this.resetToFirstStage();
        }),
        catchError(err => {
          console.error("Error creating new object:", err);
          throw new Error(err);
        })
      )
      .subscribe();
  }

  private createAddressWithoutSuggestion(): Observable<Address | null> {
    const coord = this.baseMapService.currentSelectedMarkerCoordinate.Get(EPSGType.LonLat);

    return this.permissionService
      .getCountryByCoordinates({
        latitude: coord.y,
        longitude: coord.x
      })
      .pipe(
        map(country => {
          if (!country) return undefined;

          const newAddress: Address = {
            coordinate: {
              latitude: coord.y,
              longitude: coord.x,
              country: country
            }
          };

          return newAddress;
        }),
        catchError(err => {
          console.error("Error creating address without suggestion:", err);
          throw new Error(err);
        })
      );
  }

  goBackToSuggestions() {
    this.headerLocationService.showAddressLookup.set(false);
    this.headerLocationService.selectedSuggestion.set(null);
    this.headerLocationService.lookupResponse.set(null);
    this.headerLocationService.isLoadingLookup.set(false);
    this.headerLocationService.hasLookupError.set(false);
    this.headerLocationService.showSuggestions.set(true);
  }

  // Triggers the addressChanged subject (for debounce)
  addressChanged(event: KeyboardEvent) {
    if (event.code === "ArrowUp") {
      event.preventDefault();
      this.selectPreviousSuggestion();
      return;
    }

    if (event.code === "ArrowDown") {
      event.preventDefault();
      this.selectNextSuggestion();
      return;
    }

    if (event.code === "Enter") {
      event.preventDefault();
      return;
    }

    if (event.code === "Escape") {
      event.preventDefault();
      if (this.headerLocationService.showAddressLookup()) {
        this.goBackToSuggestions();
      }
      return;
    }

    // Only trigger search if it's a character key, not a control key
    if (!event.ctrlKey && !event.altKey && !event.metaKey) {
      // Reset to first stage when typing
      if (
        this.headerLocationService.showAddressLookup() ||
        this.headerLocationService.isLoadingLookup() ||
        this.headerLocationService.hasLookupError()
      ) {
        this.resetToFirstStage();
      }
      this.addressSearchChangedSubject.next(this.internalSearchTerm());
    }
  }

  selectNextSuggestion(): void {
    const suggestions = this.headerLocationService.suggestions();
    if (!suggestions || suggestions.length === 0) {
      return;
    }

    if (this.selectedIndex === suggestions.length - 1) {
      this.selectedIndex = 0;
    } else {
      this.selectedIndex++;
    }
  }

  selectPreviousSuggestion(): void {
    const suggestions = this.headerLocationService.suggestions();
    if (!suggestions || suggestions.length === 0) {
      return;
    }

    if (this.selectedIndex === 0) {
      this.selectedIndex = suggestions.length - 1;
    } else {
      this.selectedIndex--;
    }
  }

  selectAddressByAutocomplete(event: MatAutocompleteSelectedEvent | KeyboardEvent): void {
    let selectedAddress: SuggestionAddress | null = null;

    if (event instanceof MatAutocompleteSelectedEvent) {
      selectedAddress = event.option.value;
      // Prevent the default behavior of the autocomplete
      event.option.deselect();
    }

    if (selectedAddress && !this.headerLocationService.showAddressLookup()) {
      this.setAddress(selectedAddress);
    }
  }

  clearSearch() {
    this.selectedIndex = 0;
    this.searchTermChanged.emit(null);
    this.internalSearchTerm.set("");
    this.resetToFirstStage();
    this.closeAutocomplete();
  }

  openAutocompleteIfSuggestions(): void {
    const suggestions = this.headerLocationService.suggestions();
    if (
      (suggestions && suggestions.length > 0) ||
      this.headerLocationService.showAddressLookup() ||
      this.headerLocationService.isLoadingLookup() ||
      this.headerLocationService.hasLookupError()
    ) {
      this.openAutocomplete();
    }
  }

  private resetToFirstStage() {
    this.headerLocationService.showAddressLookup.set(false);
    this.headerLocationService.selectedSuggestion.set(null);
    this.headerLocationService.lookupResponse.set(null);
    this.headerLocationService.isLoadingLookup.set(false);
    this.headerLocationService.hasLookupError.set(false);
  }

  private subscribeToAddressChanged() {
    this.addressSearchChanged$
      .pipe(
        switchMap(searchTerm => {
          if (!this.headerLocationService.isGlobalSearchMode()) {
            const centerMapCoordinates = olProj.transform(this.baseMapService.map.getView().getCenter(), "EPSG:3857", "EPSG:4326");
            const coordinate = new Coordinate([centerMapCoordinates[0], centerMapCoordinates[1]], EPSGType.LonLat);

            return this.permissionService
              .getCountryByCoordinates({
                latitude: coordinate.Get(EPSGType.LonLat).y,
                longitude: coordinate.Get(EPSGType.LonLat).x
              })
              .pipe(map(country => ({ searchTerm, country })));
          }

          return of({ searchTerm, country: null });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(coordinateResult => {
        this.searchTermChanged.emit({
          searchTerm: coordinateResult.searchTerm,
          isGlobalSearch: this.headerLocationService.isGlobalSearchMode(),
          country: coordinateResult.country
        });
      });
  }

  private openAutocomplete() {
    if (this.autocompleteTrigger()) {
      setTimeout(() => this.autocompleteTrigger().openPanel());
    }
  }

  private closeAutocomplete() {
    const autocompleteTrigger = this.autocompleteTrigger();
    if (autocompleteTrigger?.panelOpen) {
      autocompleteTrigger.closePanel();
    }
  }

  // Display function for the autocomplete
  displayFn(option: SuggestionAddress | string): string {
    // If the option is a string, return it directly
    if (typeof option === "string") {
      return option;
    }
    // If it's a SuggestionAddress object, return its Description
    return option ? option.description : "";
  }

  // Getter for the displayFn to use in the template
  get displayWithFn() {
    return this.displayFn.bind(this);
  }

  onFocus() {
    this.isFocused = true;
  }

  onBlur() {
    this.isFocused = false;
  }
}
